// src/components/common/EntityListContainer.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

// Import EntityListPage component
import EntityListPage from '../layout/EntityListPage';
import EntityTableView from '../ui/EntityTableView';
import { Grid } from '@mui/material';

/**
 * A container component that handles all the logic for entity listings
 * Works with Zustand stores and direct API calls
 */
const EntityListContainer = ({
  title,
  description,
  fetchData,
  renderEntity,
  initialFilters = {},
  initialSort = { field: 'name', direction: 'asc' },
  filterConfig = [],
  sortOptions = [],
  emptyState = {},
  actions = {},
  children,
  // View options
  availableViews = ['grid', 'table'],
  defaultView = 'grid',
  // Table configuration
  tableConfig = {
    columns: [],
    getRowData: (item) => ({}),
    renderCustomRow: null,
    onRowClick: null,
    renderRowActions: null
  },
  // Allow direct overrides of state for Zustand integration
  entities,
  isLoading: externalLoading,
  error: externalError,
  metadata: externalMetadata,
  // Handlers for external store integration
  onFiltersChange,
  onPaginationChange,
  activeFilters: externalActiveFilters,
  showActiveFilters = true,
  // Current filter values from external store
  currentFilters
}) => {
  // Keep the fetchData function in a ref to prevent it changing identity
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData; // Update ref if fetchData changes
  
  // State for data and loading
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState({ totalCount: 0, totalPages: 1 });
  
  // State for pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // State for filters and search
  const [filters, setFilters] = useState(initialFilters);
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for sorting
  const [sortField, setSortField] = useState(initialSort.field);
  const [sortDirection, setSortDirection] = useState(initialSort.direction);

  // State for view mode - use localStorage to persist the view preference
  const getInitialViewMode = () => {
    const savedView = localStorage.getItem(`${title ? title.toLowerCase() : 'entity'}_view_mode`);
    if (savedView && availableViews.includes(savedView)) {
      return savedView;
    }
    return availableViews.includes(defaultView) ? defaultView : availableViews[0];
  };
  
  const [viewMode, setViewMode] = useState(getInitialViewMode());
  
  // Latest request ID to handle race conditions
  const requestIdRef = useRef(0);
  
  // Fetch data implementing proper loading states and race condition handling
  const fetchItems = useCallback(async (manualParams = {}) => {
    setLoading(true);
    setError(null);
    
    const thisRequestId = ++requestIdRef.current;
    
    try {
      // Clean filter params before sending to API (don't send 'all' values)
      const cleanFilters = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          cleanFilters[key] = value;
        }
      });
      
      // Combine parameters from all sources
      const queryParams = {
        page,
        limit: pageSize,
        ...cleanFilters,
        ...(searchTerm ? { search: searchTerm } : {}),
        sort: `${sortDirection === 'desc' ? '-' : ''}${sortField}`,
        ...manualParams // Allow overriding with manual params
      };
      
      // Call the fetchData function (from ref to prevent stale values)
      const response = await fetchDataRef.current(queryParams);
      
      // Avoid race conditions by only updating state if this is still the latest request
      if (thisRequestId === requestIdRef.current) {
        // Extract data and metadata, handling different response structures
        
        // Backend returns meta: {limit: 10, page: 1, total: 11, totalPages: 2}
        let items = [];
        let meta = { totalCount: 0, totalPages: 1 };
        
        if (Array.isArray(response)) {
          // Case 1: Direct array of items
          items = response;
          meta.totalCount = items.length;
        } 
        else if (response && response.meta) {
          // Case 2: Response with meta object
          items = response.data || response.items || response.results || [];
          meta.totalCount = response.meta.total || 0;
          meta.totalPages = response.meta.totalPages || 1;
        } 
        else if (response && (response.data || response.items || response.results)) {
          // Case 3: Response with data but no meta
          items = response.data || response.items || response.results;
          meta.totalCount = response.totalCount || response.total || items.length;
          meta.totalPages = response.totalPages || Math.ceil(meta.totalCount / pageSize) || 1;
        }
        
        setData(items);
        setMetadata(meta);
        setLoading(false);
      }
      
      return response;
    } catch (err) {
      // Only set error if this is still the latest request
      if (thisRequestId === requestIdRef.current) {
        setError(err.message || 'An error occurred while fetching data');
        setLoading(false);
      }
      // Don't throw - let stores handle error state and notifications
      console.error('[EntityListContainer] Error fetching data:', err);
    }
  }, [page, pageSize, searchTerm, sortField, sortDirection, JSON.stringify(filters)]);
  
  // Initial data fetch
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);
  
  // Update pagination based on metadata
  useEffect(() => {
    // If external metadata is provided, update pagination when it changes
    if (externalMetadata) {
      const totalCount = externalMetadata.total || externalMetadata.totalCount || 0;
      const totalPages = externalMetadata.totalPages || Math.ceil(totalCount / pageSize) || 1;
      setMetadata({ totalCount, totalPages });
    }
  }, [externalMetadata, pageSize]);
  
  // Handle page change
  const handlePageChange = (event, newPage) => {
    if (onPaginationChange) {
      // Use external handler for Zustand integration
      onPaginationChange({ page: newPage });
    } else {
      setPage(newPage);
    }
  };
  
  // Handle search change
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPage(1); // Reset to first page when search changes
  };
  
  // Handle filter change
  const handleFilterChange = (name, value) => {
    if (onFiltersChange) {
      // Use external handler for Zustand integration
      onFiltersChange({ [name]: value });
    } else {
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
      setPage(1); // Reset to first page when filter changes
    }
  };
  
  // Handle clear all filters
  const handleClearAllFilters = () => {
    if (onFiltersChange) {
      // Use external handler for Zustand integration - clear all filters
      onFiltersChange({});
    } else {
      setFilters(initialFilters);
      setSearchTerm('');
      setPage(1);
    }
  };
  
  // Handle sort change
  const handleSortChange = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1); // Reset to first page when sort changes
  };

  // Handle view mode change
  const handleViewModeChange = (newViewMode) => {
    if (availableViews.includes(newViewMode)) {
      setViewMode(newViewMode);
      // Persist the view preference to localStorage
      localStorage.setItem(`${title ? title.toLowerCase() : 'entity'}_view_mode`, newViewMode);
    }
  };

  // Handle row click in table view
  const handleTableRowClick = (item) => {
    if (tableConfig.onRowClick) {
      tableConfig.onRowClick(item);
    }
  };
  
  // Use external state values if provided, otherwise use local state
  const displayEntities = entities !== undefined ? entities : data;
  const displayLoading = externalLoading !== undefined ? externalLoading : loading;
  const displayError = externalError !== undefined ? externalError : error;
  
  // For metadata, we need to handle the specific format
  let displayMetadata = metadata;
  if (externalMetadata) {
    displayMetadata = {
      totalCount: externalMetadata.total || externalMetadata.totalCount || 0,
      totalPages: externalMetadata.totalPages || 1
    };
  }
  
  // Use external active filters if provided, otherwise build them
  let activeFilters = externalActiveFilters || [];
  
  if (!externalActiveFilters) {
    // Create active filters directly here in the format ActiveFilters expects
    activeFilters = [];
    
    // IMPORTANT: Do NOT include search term in activeFilters
    // The ActiveFilters component will add it automatically from searchTerm prop
    
    // Use currentFilters if provided (external state), otherwise use local filters
    const filtersToUse = currentFilters || filters;
    
    Object.entries(filtersToUse).forEach(([key, value]) => {
      if (value !== 'all' && value !== '' && value !== null && value !== undefined) {
        const config = filterConfig.find(config => config.name === key);
        const label = config ? config.label : key;
        const displayValue = config?.options?.find(opt => opt.value === value)?.label || value;
        
        activeFilters.push({
          key,
          label: `${label}: ${displayValue}`,
          onDelete: () => handleFilterChange(key, 'all')
        });
      }
    });
  }

  // Render the appropriate view based on view mode
  const renderView = () => {
    if (viewMode === 'table' && tableConfig.columns.length > 0) {
      return (
        <EntityTableView
          items={displayEntities}
          columns={tableConfig.columns}
          renderTableRow={tableConfig.renderCustomRow}
          onRowClick={tableConfig.onRowClick}
          renderRowActions={tableConfig.renderRowActions}
          onActionClick={(e, item) => {
            // Handle action menu opening
            if (item.menuButtonProps?.onClick) {
              item.menuButtonProps.onClick(e);
            }
          }}
          keyExtractor={(item) => item._id || item.id}
        />
      );
    }
    
    // Default to grid view
    return (
      <Grid container spacing={3}>
        {displayEntities.map((entity, index) => (
          <Grid item xs={12} sm={6} md={4} key={entity._id || entity.id || index}>
            {renderEntity(entity, index)}
          </Grid>
        ))}
      </Grid>
    );
  };
  
  return (
    <EntityListPage
      title={title}
      description={description}
      entities={displayEntities}
      isLoading={displayLoading}
      error={displayError}
      
      pagination={{
        page,
        pageSize,
        totalPages: displayMetadata.totalPages || 1,
        onPageChange: handlePageChange
      }}
      
      filters={{
        searchTerm,
        filterValues: currentFilters || filters,
        onSearchChange: handleSearchChange,
        onClearSearch: () => handleSearchChange(''),
        onFilterChange: handleFilterChange,
        onClearAllFilters: handleClearAllFilters,
        filterConfig,
        activeFilters,
        showActiveFilters
      }}
      
      sort={{
        field: sortField,
        direction: sortDirection,
        onSortChange: handleSortChange,
        sortOptions
      }}
      
      view={{
        current: viewMode,
        available: availableViews,
        onChange: handleViewModeChange,
        content: displayEntities.length > 0 ? renderView() : null
      }}
      
      renderEntity={renderEntity}
      actions={actions}
      emptyState={emptyState}
    >
      {children}
    </EntityListPage>
  );
};

EntityListContainer.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  fetchData: PropTypes.func.isRequired,
  renderEntity: PropTypes.func.isRequired,
  initialFilters: PropTypes.object,
  initialSort: PropTypes.shape({
    field: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc'])
  }),
  filterConfig: PropTypes.array,
  sortOptions: PropTypes.array,
  emptyState: PropTypes.object,
  actions: PropTypes.object,
  children: PropTypes.node,
  
  // View options
  availableViews: PropTypes.arrayOf(PropTypes.oneOf(['grid', 'table'])),
  defaultView: PropTypes.oneOf(['grid', 'table']),
  
  // Table configuration
  tableConfig: PropTypes.shape({
    columns: PropTypes.array,
    getRowData: PropTypes.func,
    renderCustomRow: PropTypes.func,
    onRowClick: PropTypes.func
  }),
  
  // Direct state overrides for Zustand integration
  entities: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.any,
  metadata: PropTypes.object,
  onFiltersChange: PropTypes.func,
  onPaginationChange: PropTypes.func,
  activeFilters: PropTypes.array,
  showActiveFilters: PropTypes.bool,
  currentFilters: PropTypes.object
};

export default EntityListContainer;